import { useToast } from '@chakra-ui/react';
import type { UTCDate } from '@date-fns/utc';
import { isNil } from 'lodash';
import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'urql';

import { CenterLoading, FeatureFlag } from '@revelio/core';
import {
  type CustomRoleTaxonomySelection,
  ROLE_SELECTION_LIST_QUERY,
  TaxonomyType,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useCurrentRoleTaxonomyVersion as useAdapterCurrentRoleTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { useSelectionListState } from '../../../../shared/selection-lists/hooks/useSelectionListState';
import { useTaxonomyTree } from '../../../../shared/selection-lists/hooks/useTaxonomyTree';
import {
  type AllSelectionListsWithSkillTaxonomyKey,
  type GqlSelectionListKey,
  gqlSelectionListKeys,
  type RoleSelectionListKey,
  type RoleSelectionLists,
  type SelectionListKey,
  SKILL_TAXONOMY_SELECTION_LIST_KEYS,
} from '../../../../shared/selection-lists/types';
import { getRoleTaxonomyId } from '../../../../shared/selection-lists/utils/roleTaxonomy';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import {
  PreferenceScope,
  useUserPreferences,
} from '../../../../shared/user-preferences';
import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  createInitialFilterConsistencyState,
  createNewFilterState,
  type FilterConsistencyState,
  getActiveFilterMap,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import { type FilterAction, filterReducer } from '../engine/filterReducer';
import {
  getFilterDefinition,
  getFilterHandler,
} from '../engine/filterRegistry';
import { useAdaptiveRoleTaxonomy } from '../hooks/useAdaptiveRoleTaxonomy';
import { useAdaptiveSkillTaxonomy } from '../hooks/useAdaptiveSkillTaxonomy';
import { useAllSelectionLists } from '../hooks/useAllSelectionLists';
import { useCurrentSkillTaxonomyVersion } from '../hooks/useCurrentSkillTaxonomyVersion';
import { useDeserializeFilterFromUrl } from '../hooks/useDeserializeFilterFromUrl';
import {
  type GeographyMode,
  useGeographyMode,
} from '../hooks/useGeographyMode';
import {
  applyGeographyModeToFilterMap,
  getGeographySelectionListIds,
  useGeographyModeChangeEffect,
} from '../hooks/useGeographyModeSync';
import { useCurrentRoleTaxonomyVersion as useLegacyCurrentRoleTaxonomyVersion } from '../hooks/useLatestTaxonomyVersion';
import { useSupportsStateGeography } from '../hooks/useSupportsStateGeography';
import { useSyncFiltersToUrl } from '../hooks/useSyncFiltersToUrl';
import { sortCitySelectionList } from '../selectionList/sortCitySelectionList';
import {
  type AllSelectionListKeys,
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type PrimaryFilterId,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import { flattenTreeToSelectionLists } from '../utils/flattenTreeToSelectionLists';
import { applyResolvedSelectionLabelsToFilterMap } from '../utils/patchUnresolvedSelectionNames';
import {
  isRicsCompanyFilter,
  pruneTreeToDepth,
  RICS_COMPANY_SELECTION_LIST_IDS,
  RICS_INDUSTRY_SELECTION_LIST_IDS,
  RICS_TAXONOMY_ID,
  RICS_TAXONOMY_TREE_LEVELS,
  stripTreeNodeTooltipData,
} from '../utils/ricsTaxonomyTree';
import { selectionListCache } from '../utils/selectionListCache';
import type { CustomSkillTreeNode } from '../utils/skillTaxonomyTree';
import { toNestedTaxonomyTree } from '../utils/taxonomyNestedTree';
import type { DefaultSelectionOverride } from './defaultFilters';
import {
  getSharedFilterState,
  setSharedFilterState,
} from './sharedFilterState';
import { useSyncMetricModePostingSource } from './useSyncMetricModePostingSource';

export interface DateRangeBounds {
  min: UTCDate;
  max: UTCDate;
}

export type DateGranularity = 'week' | 'month';

export type WeekMonthDateRanges = {
  [key in DateGranularity]: DateRangeBounds;
};

export interface TreeData {
  id: string;
  name: string;
  children?: TreeData[];
  depth?: number;
  levelId?: AllSelectionListsWithSkillTaxonomyKey;
  nodeCount?: number;
  info?: string;
  suggestedTitles?: string[];
  taxonomyTitles?: string | readonly (string | null)[] | null;
}

export interface ListData {
  selectionListId: SelectionListKey;
  selectionList: { id: string; shortName: string; longName: string }[];
}

export type FilterDataResult = TreeData[] | ListData[];

const ROLE_TAXONOMY_TREE_LEVELS = [1, 3, 5, 7];
const ROLE_TAXONOMY_SELECTION_LIST_IDS: RoleSelectionListKey[] = [
  'role_k7',
  'role_k150',
  'role_k1500',
  'role_k15000',
];
const GEOGRAPHY_TAXONOMY_TREE_LEVELS = [1, 2, 3, 4];
const GEOGRAPHY_TAXONOMY_ID_BY_MODE: Record<GeographyMode, number> = {
  msa: 1,
  administrative: 2,
};

interface FilterContextType {
  filterState: FilterConsistencyState;
  activeFilterMap: FilterSelectionsMap;
  dispatch: React.Dispatch<FilterAction>;
  getFilterData: (filterId: FilterId) => FilterDataResult;
  hadInitialUrlFilters: boolean;
  defaultSelectionOverrides?: DefaultSelectionOverride;
  /** True once adaptive taxonomy hooks have resolved (always true for non-company pages). */
  isTaxonomyResolved: boolean;
  /** Current geography granularity — exposed so consumers can react to mode changes. */
  geographyMode: GeographyMode;
}

const FilterContext = createContext<FilterContextType | null>(null);

/** Provides a no-op FilterContext for read-only contexts (e.g. shared report viewer). */
export const StubFilterProvider = ({ children }: PropsWithChildren) => {
  const stubValue: FilterContextType = {
    filterState: {
      activeKey: '',
      groups: {},
    } as unknown as FilterConsistencyState,
    activeFilterMap: new Map(),
    dispatch: () => undefined,
    getFilterData: () => [],
    hadInitialUrlFilters: false,
    isTaxonomyResolved: true,
    geographyMode: 'msa' as GeographyMode,
  };

  return (
    <FilterContext.Provider value={stubValue}>
      {children}
    </FilterContext.Provider>
  );
};

type FilterProviderProps = PropsWithChildren & {
  pageId: string;
  revelioPageId?: RevelioPageId;
  primaryFilterId?: PrimaryFilterId;
  primaryFilterSelectionLimit?: number;
  primaryFilterSelectionDefault?: number;
  defaultSelectionOverrides?: DefaultSelectionOverride;
  /**
   * When false, initial state ignores URL `filters` / `filterGroups` (isolated instances, e.g. AI chart preview).
   * Defaults true for normal dashboard routes.
   */
  hydrateFromUrl?: boolean;
  /**
   * When false, filter changes are not written to the URL.
   * Defaults true for normal dashboard routes.
   */
  syncFiltersToUrl?: boolean;
  /**
   * When false, this provider's state is isolated — it does not hydrate from
   * the in-memory shared filter state and does not write back to it.
   * Required for mounts whose state must stay separate (Report Builder, AI
   * chart preview). Defaults true for normal dashboard routes.
   */
  persistScope?: boolean;
};

const FilterProviderReferenceApiBootstrap = (props: FilterProviderProps) => {
  const {
    version: roleTaxonomyVersion,
    loading: isRoleTaxonomyVersionLoading,
  } = useAdapterCurrentRoleTaxonomyVersion();

  const {
    version: skillTaxonomyVersion,
    getSpecificTaxonomyVersion: getSpecificSkillTaxonomyVersion,
    loading: isSkillTaxonomyVersionLoading,
  } = useCurrentSkillTaxonomyVersion();

  const { isInitialLoading: isPreferencesLoading } = useUserPreferences(
    PreferenceScope.GLOBAL
  );

  if (
    isRoleTaxonomyVersionLoading ||
    isSkillTaxonomyVersionLoading ||
    isPreferencesLoading
  ) {
    return <CenterLoading />;
  }

  return (
    <FilterProviderWithTaxonomyVersion
      {...props}
      roleTaxonomyVersion={roleTaxonomyVersion}
      skillTaxonomyVersion={skillTaxonomyVersion}
      getSpecificSkillTaxonomyVersion={getSpecificSkillTaxonomyVersion}
    />
  );
};

const FilterProviderLegacyRoleTaxonomyBootstrap = (
  props: FilterProviderProps
) => {
  const {
    version: roleTaxonomyVersion,
    loading: isRoleTaxonomyVersionLoading,
  } = useLegacyCurrentRoleTaxonomyVersion();

  const {
    version: skillTaxonomyVersion,
    getSpecificTaxonomyVersion: getSpecificSkillTaxonomyVersion,
    loading: isSkillTaxonomyVersionLoading,
  } = useCurrentSkillTaxonomyVersion();

  const { isInitialLoading: isPreferencesLoading } = useUserPreferences(
    PreferenceScope.GLOBAL
  );

  if (
    isRoleTaxonomyVersionLoading ||
    isSkillTaxonomyVersionLoading ||
    isPreferencesLoading
  ) {
    return <CenterLoading />;
  }

  return (
    <FilterProviderWithTaxonomyVersion
      {...props}
      roleTaxonomyVersion={roleTaxonomyVersion}
      skillTaxonomyVersion={skillTaxonomyVersion}
      getSpecificSkillTaxonomyVersion={getSpecificSkillTaxonomyVersion}
    />
  );
};

export const FilterProvider = (props: FilterProviderProps) => {
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);

  if (useReferenceApi) {
    return <FilterProviderReferenceApiBootstrap {...props} />;
  }

  return <FilterProviderLegacyRoleTaxonomyBootstrap {...props} />;
};
/**
 * Provides filter state and dispatch actions to the entire application.
 * Filters contain raw user selections. Dynamic defaults are provided through useEffectiveFilters.
 */
const FilterProviderWithTaxonomyVersion = ({
  pageId,
  revelioPageId,
  primaryFilterId,
  primaryFilterSelectionLimit = 6,
  primaryFilterSelectionDefault = 6,
  defaultSelectionOverrides,
  hydrateFromUrl = true,
  syncFiltersToUrl: syncFiltersToUrlProp = true,
  persistScope = true,
  roleTaxonomyVersion,
  skillTaxonomyVersion,
  children,
}: FilterProviderProps & {
  roleTaxonomyVersion?: string;
  skillTaxonomyVersion?: string;
  getSpecificSkillTaxonomyVersion: (
    taxonomySelection: CustomSkillTaxonomySelection
  ) => string | undefined;
}) => {
  const location = useLocation();
  const toast = useToast();
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);

  const { filterFromUrl, urlFiltersParseFailed } = useDeserializeFilterFromUrl({
    pageId: revelioPageId ?? pageId,
    primaryFilterId,
    primaryFilterSelectionLimit,
    taxonomyConfig: {
      roleTaxonomyVersion,
      skillTaxonomyVersion,
    },
    defaultSelections: defaultSelectionOverrides,
    hydrateFromUrl,
  });

  const [hadInitialUrlFilters] = useState(() => {
    if (!hydrateFromUrl) {
      return false;
    }
    const params = new URLSearchParams(location.search);
    return params.has('filters') || params.has('filterGroups');
  });

  useEffect(() => {
    if (!urlFiltersParseFailed) return;
    const toastId = 'invalid-filters-from-url';
    if (toast.isActive(toastId)) return;
    toast({
      id: toastId,
      title: 'Could not load filters from the link',
      description:
        'The filters in this URL are invalid or corrupted. Default filters are being used instead.',
      status: 'warning',
      duration: 8000,
      isClosable: true,
      position: 'top-right',
    });
  }, [urlFiltersParseFailed, toast]);

  const { selectionLists } = useSelectionListState();
  const allSelectionLists = useAllSelectionLists();

  const initialFilterState = (() => {
    const shared = getSharedFilterState();
    if (persistScope && shared) return shared;

    if (filterFromUrl) return filterFromUrl;

    if (!primaryFilterId) {
      return createInitialFilterConsistencyState(
        NO_PRIMARY_FILTER_KEY,
        { roleTaxonomyVersion, skillTaxonomyVersion },
        defaultSelectionOverrides
      );
    }

    return createNewFilterState({
      primaryFilterId,
      pageId,
      selectionDefault: primaryFilterSelectionDefault,
      taxonomyConfig: { roleTaxonomyVersion, skillTaxonomyVersion },
      ricsK400List: selectionLists?.rics_k400,
      defaultSelections: defaultSelectionOverrides,
    });
  })();

  const [filterState, dispatch] = useReducer(filterReducer, initialFilterState);

  // Write-through to the in-memory shared state on every change so the next
  // FilterProvider mount within the same session can hydrate from it.
  useEffect(() => {
    if (!persistScope) return;
    setSharedFilterState(filterState);
  }, [filterState, persistScope]);

  // The adaptive taxonomy hook returns { isResolved } — true once it has
  // determined the taxonomy (dispatched or verified). This is batched with
  // the taxonomy dispatch in the same React render, so queries only unpause
  // once the correct taxonomy is in filterState.
  const { isResolved: isTaxonomyResolved } = useAdaptiveRoleTaxonomy({
    primaryFilterId,
    filterState,
    dispatch,
    revelioPageId,
  });
  useSyncMetricModePostingSource({ filterState, dispatch });

  useAdaptiveSkillTaxonomy({
    primaryFilterId,
    filterState,
    dispatch,
    revelioPageId,
  });

  const activeFilterMap = getActiveFilterMap(filterState, {
    roleTaxonomyVersion,
    skillTaxonomyVersion,
  });

  const normalizedActiveFilterMap: FilterSelectionsMap = (() => {
    const map = new Map(activeFilterMap);

    const isTreeFilter = (f: Filter | undefined): f is TreeFilter =>
      Boolean(f && f.type === FilterType.TREE);

    const customDef = getFilterDefinition(FilterId.CUSTOM_SKILL);
    if (!customDef) return map;

    const existingCustom = map.get(FilterId.CUSTOM_SKILL);
    const skill = map.get(FilterId.SKILL);

    // Always remove legacy SKILL entry when feature flag is on
    map.delete(FilterId.SKILL);

    // If CUSTOM_SKILL already has selections, keep as-is
    if (
      isTreeFilter(existingCustom) &&
      Array.isArray(existingCustom.selections) &&
      existingCustom.selections.length > 0
    ) {
      return map;
    }

    // Otherwise, if SKILL had selections, remap them into CUSTOM_SKILL
    if (
      isTreeFilter(skill) &&
      Array.isArray(skill.selections) &&
      skill.selections.length > 0
    ) {
      const selectionListIds = customDef.selectionListIds;
      const remappedSelections = skill.selections.map((sel) => {
        const depth = String(sel.id).split('_').length - 1;
        const newLevelId = selectionListIds[depth] ?? sel.levelId;
        return { ...sel, levelId: newLevelId };
      });

      const remappedCustom: TreeFilter = {
        id: FilterId.CUSTOM_SKILL,
        name: customDef.name,
        type: customDef.type,
        isVisible: customDef.isVisible,
        selectionListIds: customDef.selectionListIds,
        selections: remappedSelections,
      };

      map.set(FilterId.CUSTOM_SKILL, remappedCustom);
      return map;
    }

    // No selections to migrate; keep map (with SKILL removed)
    return map;
  })();

  const rawGeographyMode = useGeographyMode();
  const supportsStateGeography = useSupportsStateGeography(revelioPageId);

  // When the page doesn't support state geography but the user's preference is
  // administrative, override to MSA for this page's rendering and data fetching.
  // The global preference stays untouched.
  const geographyMode: GeographyMode =
    !supportsStateGeography && rawGeographyMode === 'administrative'
      ? 'msa'
      : rawGeographyMode;

  const geographyModeRef = useRef(geographyMode);
  geographyModeRef.current = geographyMode;

  const geographyAdjustedActiveFilterMap = applyGeographyModeToFilterMap(
    normalizedActiveFilterMap,
    geographyMode
  );

  useGeographyModeChangeEffect({
    geographyMode: rawGeographyMode,
    normalizedActiveFilterMap,
    filterStateActiveKey: filterState.activeKey,
    revelioPageId,
    primaryFilterSelectionDefault,
    pageId,
    dispatch,
  });

  // Type guards to refine filters from the selections map
  const isSingleValueFilter = <T,>(
    f: Filter | undefined,
    id: FilterId
  ): f is SingleValueFilter<T> =>
    Boolean(f && f.type === FilterType.VALUE && f.id === id);

  // Resolve current numeric skill taxonomy id from active filters so selection list fetches reflect UI changes
  const skillTaxonomyFilter = activeFilterMap.get(FilterId.SKILL_TAXONOMY);
  const skillTaxonomyFilterResolved =
    isSingleValueFilter<CustomSkillTaxonomySelection>(
      skillTaxonomyFilter,
      FilterId.SKILL_TAXONOMY
    )
      ? skillTaxonomyFilter
      : undefined;
  const skillTaxonomyOverrideId = skillTaxonomyFilterResolved?.value.taxonomyId;
  const skillTaxonomyVersionOverride =
    skillTaxonomyFilterResolved?.value.version;

  const { customSkillTree, skillTaxonomyReferenceNestedTree } =
    useCustomSkillTaxonomyTree(
      skillTaxonomyOverrideId,
      skillTaxonomyVersionOverride
    );

  const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
  const roleTaxonomyValue = isSingleValueFilter<CustomRoleTaxonomySelection>(
    roleTaxonomyFilter,
    FilterId.ROLE_TAXONOMY
  )
    ? roleTaxonomyFilter.value
    : undefined;
  const shouldPauseRoleQuery = isNil(roleTaxonomyValue);
  const roleReferenceTaxonomyId = useMemo(
    () => getRoleTaxonomyId(roleTaxonomyValue),
    [roleTaxonomyValue]
  );
  const {
    treeData: rawRoleReferenceTree,
    fetching: isRoleReferenceTreeFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Role,
    taxonomyId: roleReferenceTaxonomyId,
    levels: ROLE_TAXONOMY_TREE_LEVELS,
    pause: !useReferenceApi || roleReferenceTaxonomyId === undefined,
  });
  const roleReferenceTree = useMemo(
    () =>
      toNestedTaxonomyTree(
        rawRoleReferenceTree,
        ROLE_TAXONOMY_SELECTION_LIST_IDS,
        undefined,
        0,
        true,
        false
      ),
    [rawRoleReferenceTree]
  );
  const geographySelectionListIds = useMemo(
    () => getGeographySelectionListIds(geographyMode),
    [geographyMode]
  );
  const {
    treeData: rawGeographyReferenceTree,
    fetching: isGeographyReferenceTreeFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Geography,
    taxonomyId: GEOGRAPHY_TAXONOMY_ID_BY_MODE[geographyMode],
    levels: GEOGRAPHY_TAXONOMY_TREE_LEVELS,
    pause: !useReferenceApi,
  });
  const geographyReferenceTree = useMemo(
    () =>
      toNestedTaxonomyTree(
        rawGeographyReferenceTree,
        geographySelectionListIds,
        undefined,
        0,
        false,
        true
      ),
    [geographySelectionListIds, rawGeographyReferenceTree]
  );
  const {
    treeData: rawRicsReferenceTree,
    fetching: isRicsReferenceTreeFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Rics,
    taxonomyId: RICS_TAXONOMY_ID,
    levels: RICS_TAXONOMY_TREE_LEVELS,
    pause: !useReferenceApi,
  });
  const ricsCompanyReferenceTree = useMemo(
    () =>
      stripTreeNodeTooltipData(
        toNestedTaxonomyTree(
          rawRicsReferenceTree,
          RICS_COMPANY_SELECTION_LIST_IDS,
          undefined,
          0,
          true,
          false
        )
      ),
    [rawRicsReferenceTree]
  );
  const ricsIndustryReferenceTree = useMemo(
    () =>
      pruneTreeToDepth(
        ricsCompanyReferenceTree,
        RICS_INDUSTRY_SELECTION_LIST_IDS.length - 1
      ),
    [ricsCompanyReferenceTree]
  );

  const roleTaxonomyVariables = React.useMemo(() => {
    if (!roleTaxonomyValue) return { taxonomyId: { default: true } };
    const { version: _unused, ...rest } = roleTaxonomyValue;
    return { taxonomyId: rest };
  }, [roleTaxonomyValue]);

  const [
    {
      data: roleSelectionListsResponse,
      fetching: isRoleSelectionListsFetching,
    },
  ] = useQuery({
    query: ROLE_SELECTION_LIST_QUERY,
    variables: roleTaxonomyVariables,
    pause: shouldPauseRoleQuery || useReferenceApi,
    context: useMemo(
      () =>
        primaryFilterId === FilterId.ROLE
          ? undefined
          : { priority: 'low' as const },
      [primaryFilterId]
    ),
  });
  const roleSelectionLists =
    roleSelectionListsResponse?.selectionList as RoleSelectionLists;

  // Merge the internally-fetched role selection lists (keyed to the CURRENT
  // filter-state ROLE_TAXONOMY) into the selection list set handed to the
  // label patcher. `useAllSelectionLists` pulls its role lists through
  // UserPreferencesContext, which — inside `FilterProvider` — sees the outer
  // default GLOBAL preferences (role = adaptive, no effective) and returns
  // no role lists for adaptive users. Without this merge, the label patcher
  // has no role data to re-resolve against, so role selections dispatched
  // by the AI apply with raw numeric names never get a human-readable
  // label once the taxonomy settles. See the companion comment in
  // `transformAiResponseToCustomDashFilters/transformValue.ts` (role branch)
  // for why the transformer intentionally leaves role selection names as
  // raw numeric ids.
  const flatRoleLists = useMemo(
    () =>
      useReferenceApi && roleReferenceTree.length > 0
        ? flattenTreeToSelectionLists(
            roleReferenceTree,
            ROLE_TAXONOMY_SELECTION_LIST_IDS
          )
        : [],
    [roleReferenceTree, useReferenceApi]
  );

  const flatGeoLists = useMemo(
    () =>
      useReferenceApi && geographyReferenceTree.length > 0
        ? flattenTreeToSelectionLists(
            geographyReferenceTree,
            geographySelectionListIds
          )
        : [],
    [geographyReferenceTree, geographySelectionListIds, useReferenceApi]
  );

  const flatRicsLists = useMemo(
    () =>
      useReferenceApi && ricsCompanyReferenceTree.length > 0
        ? flattenTreeToSelectionLists(
            ricsCompanyReferenceTree,
            RICS_COMPANY_SELECTION_LIST_IDS
          )
        : [],
    [ricsCompanyReferenceTree, useReferenceApi]
  );

  const flatSkillLists = useMemo(
    () =>
      useReferenceApi &&
      skillTaxonomyReferenceNestedTree &&
      skillTaxonomyReferenceNestedTree.length > 0
        ? flattenTreeToSelectionLists(
            skillTaxonomyReferenceNestedTree,
            SKILL_TAXONOMY_SELECTION_LIST_KEYS
          )
        : [],
    [skillTaxonomyReferenceNestedTree, useReferenceApi]
  );

  const allSelectionListsWithCurrentRoleTaxonomy = useMemo(() => {
    if (useReferenceApi) {
      return [
        ...allSelectionLists,
        ...flatRoleLists,
        ...flatGeoLists,
        ...flatRicsLists,
        ...flatSkillLists,
      ] as typeof allSelectionLists;
    }

    if (!roleSelectionLists) return allSelectionLists;
    const roleListItems = Object.entries(
      roleSelectionLists as Record<string, unknown>
    )
      .filter(([id]) =>
        ['role_k7', 'role_k150', 'role_k1500', 'role_k15000'].includes(id)
      )
      .map(([id, value]) => ({ id, value }));
    if (roleListItems.length === 0) return allSelectionLists;
    const nonRole = allSelectionLists.filter(
      (l) => !String(l.id).startsWith('role_k')
    );
    return [...nonRole, ...roleListItems] as typeof allSelectionLists;
  }, [
    allSelectionLists,
    roleSelectionLists,
    useReferenceApi,
    flatRoleLists,
    flatGeoLists,
    flatRicsLists,
    flatSkillLists,
  ]);

  const activeFilterMapWithResolvedLabels =
    applyResolvedSelectionLabelsToFilterMap(
      geographyAdjustedActiveFilterMap,
      allSelectionListsWithCurrentRoleTaxonomy
    );

  const entries = Array.from(activeFilterMapWithResolvedLabels.entries());

  useSyncFiltersToUrl(
    entries,
    roleTaxonomyVersion,
    skillTaxonomyVersion,
    revelioPageId,
    syncFiltersToUrlProp
  );

  // Retrieve processed selection lists required by a filter
  const getFilterData = useCallback(
    (filterId: FilterId): FilterDataResult => {
      const definition = getFilterDefinition(filterId);
      const handler = getFilterHandler(filterId);

      let requiredLists: (GqlSelectionListKey | RoleSelectionListKey)[] = [];

      // For geography, derive selectionListIds from the current geography
      // mode (msa vs administrative). We read from a ref so that the
      // useCallback does not need geographyMode in its deps (which would
      // cause infinite re-renders via context propagation).
      if (filterId === FilterId.GEOGRAPHY) {
        requiredLists = getGeographySelectionListIds(
          geographyModeRef.current
        ) as GqlSelectionListKey[];
      } else if (
        handler &&
        typeof handler.getRequiredSelectionListIds === 'function' &&
        definition
      ) {
        // Narrowing union generic across definition/handler is not possible here.
        // Both come from the same registry keyed by filterId, so this call is safe.
        requiredLists = (
          handler.getRequiredSelectionListIds as (
            def: typeof definition
          ) => (GqlSelectionListKey | RoleSelectionListKey)[]
        )(definition);
      }

      // Contextual override: append city for Geography on eligible pages under flags
      if (filterId === FilterId.GEOGRAPHY && !requiredLists.includes('city')) {
        requiredLists = [...requiredLists, 'city'];
      }

      if (useReferenceApi && filterId === FilterId.ROLE) {
        return isRoleReferenceTreeFetching ? [] : roleReferenceTree;
      }

      if (useReferenceApi && filterId === FilterId.GEOGRAPHY) {
        return isGeographyReferenceTreeFetching ? [] : geographyReferenceTree;
      }

      if (useReferenceApi && isRicsCompanyFilter(filterId)) {
        return isRicsReferenceTreeFetching ? [] : ricsCompanyReferenceTree;
      }

      if (useReferenceApi && filterId === FilterId.INDUSTRY) {
        return isRicsReferenceTreeFetching ? [] : ricsIndustryReferenceTree;
      }

      if (!selectionLists) return [];

      if (filterId === FilterId.CUSTOM_SKILL) {
        // Hide level-4 nodes from the main filter selection UI while
        // keeping them available to consumers that call the taxonomy tree
        // directly (e.g. subfilters in charts). We prune children deeper
        // than the deepest declared selection list id for this filter.
        const def = getFilterDefinition(FilterId.CUSTOM_SKILL);
        const maxVisibleDepth = Math.max(
          0,
          (def?.selectionListIds?.length ?? 4) - 1
        );

        const pruneToDepth = (
          nodes: CustomSkillTreeNode[] | null,
          depth: number
        ): TreeData[] => {
          if (!nodes || nodes.length === 0) return [];
          return nodes.map((n) => {
            const hasChildren =
              Array.isArray(n.children) && n.children.length > 0;
            const levelId = SKILL_TAXONOMY_SELECTION_LIST_KEYS[depth];
            const baseData: TreeData = {
              id: n.id,
              name: n.name,
              levelId,
              ...(n.taxonomyTitles != null && {
                taxonomyTitles: n.taxonomyTitles,
              }),
              ...(n.suggestedTitles && { suggestedTitles: n.suggestedTitles }),
            };

            if (!hasChildren || depth >= maxVisibleDepth) {
              return baseData;
            }

            return {
              ...baseData,
              children: pruneToDepth(
                n.children as CustomSkillTreeNode[],
                depth + 1
              ),
            };
          });
        };

        return pruneToDepth(customSkillTree, 0);
      }

      if (filterId === FilterId.ROLE && roleSelectionLists) {
        const cachedTree = selectionListCache.getNestedTree(
          filterId,
          requiredLists,
          selectionLists,
          roleSelectionLists,
          roleTaxonomyValue,
          isRoleSelectionListsFetching
        );
        return cachedTree ?? [];
      }

      const gqlKeys = requiredLists.filter((k): k is GqlSelectionListKey =>
        gqlSelectionListKeys.includes(k as GqlSelectionListKey)
      );

      if (isMultiGranularityFilter(filterId)) {
        const cachedTree = selectionListCache.getNestedTree(
          filterId,
          requiredLists,
          selectionLists,
          roleSelectionLists,
          filterId === FilterId.ROLE ? roleTaxonomyValue : undefined,
          filterId === FilterId.ROLE ? isRoleSelectionListsFetching : undefined
        );
        return cachedTree ?? [];
      }

      const relevantLists = gqlKeys
        .map((listId) => {
          const list = selectionLists[listId] ?? [];
          const sortedList =
            listId === 'city' ? sortCitySelectionList(list) : list;
          return {
            selectionListId: listId,
            selectionList: sortedList,
          };
        })
        .filter((list) => list.selectionList !== undefined);

      return relevantLists as FilterDataResult;
    },
    [
      selectionLists,
      roleSelectionLists,
      customSkillTree,
      roleTaxonomyValue,
      isRoleSelectionListsFetching,
      useReferenceApi,
      roleReferenceTree,
      isRoleReferenceTreeFetching,
      geographyReferenceTree,
      isGeographyReferenceTreeFetching,
      ricsCompanyReferenceTree,
      ricsIndustryReferenceTree,
      isRicsReferenceTreeFetching,
    ]
  );

  return (
    <FilterContext.Provider
      value={{
        filterState,
        activeFilterMap: activeFilterMapWithResolvedLabels,
        dispatch,
        getFilterData,
        hadInitialUrlFilters,
        defaultSelectionOverrides,
        isTaxonomyResolved,
        geographyMode,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilterContext must be used within a FilterProvider');
  }
  return context;
};

/**
 * Returns true once adaptive taxonomy hooks have resolved for company pages.
 * Always true for non-company pages. Use to `pause` URQL queries until
 * the correct taxonomy is known.
 */
export const useIsTaxonomyResolved = (): boolean => {
  const { isTaxonomyResolved } = useFilterContext();
  return isTaxonomyResolved;
};

/**
 * Custom hook to get a function for removing a filter (reverting to its default state).
 */
export const useRemoveFilter = () => {
  const { dispatch } = useFilterContext();
  return (filterId: FilterId) => dispatch({ type: 'REMOVE_FILTER', filterId });
};

/**
 * A hook to fetch and manage the specific data required for a single filter component.
 */
export const useSingleFilterData = (filterId: FilterId) => {
  const { getFilterData, geographyMode } = useFilterContext();
  const [data, setData] = useState<FilterDataResult | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const rawData = getFilterData(filterId);
    setData(rawData?.length ? rawData : null);
    // geographyMode is included so that geography filters re-fetch tree data
    // when the mode changes, even though getFilterData reads mode via a ref.
  }, [filterId, getFilterData, geographyMode]);

  return data;
};

/**
 * Checks if a filter uses multiple selection lists (e.g., GEO with country, state, city).
 */
const isMultiGranularityFilter = <T extends FilterId>(filterId: T): boolean => {
  const definition = getFilterDefinition(filterId);
  const handler = getFilterHandler(filterId);
  let requiredLists: AllSelectionListKeys[] = [];
  if (
    handler &&
    typeof handler.getRequiredSelectionListIds === 'function' &&
    definition
  ) {
    requiredLists = (
      handler.getRequiredSelectionListIds as (
        def: typeof definition
      ) => AllSelectionListKeys[]
    )(definition);
  }
  return requiredLists.length > 1;
};

//
